import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTrainingGoals } from '@/hooks/useTrainingGoals';
import { CreateTrainingGoalData, UpdateTrainingGoalData } from '@/types/trainingGoals';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';

interface TrainingGoalModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEditing?: boolean;
  currentGoal?: number;
}

export const TrainingGoalModal: React.FC<TrainingGoalModalProps> = ({
  isOpen,
  onClose,
  isEditing = false,
  currentGoal
}) => {
  const [step, setStep] = useState(1);
  const [monthlyGoal, setMonthlyGoal] = useState(currentGoal?.toString() || '');
  const [error, setError] = useState('');

  const { createGoal, updateGoal, isCreating, isUpdating } = useTrainingGoals();

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setMonthlyGoal(currentGoal?.toString() || '');
      setError('');
    }
  }, [isOpen, currentGoal]);

  const handleNext = () => {
    if (!monthlyGoal || parseInt(monthlyGoal) < 1) {
      setError('Please enter a valid goal number');
      return;
    }
    setStep(2);
    setError('');
  };

  const handleBack = () => {
    setStep(1);
    setError('');
  };

  const handleConfirm = async () => {
    try {
      const goalNumber = parseInt(monthlyGoal);
      if (isEditing) {
        await updateGoal({ monthly_goal: goalNumber });
      } else {
        await createGoal({ monthly_goal: goalNumber });
      }
      onClose();
    } catch (error) {
      setError('Failed to save goal. Please try again.');
    }
  };

  const weeklyGoal = monthlyGoal ? Math.ceil(parseInt(monthlyGoal) / 4) : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="w-[90vw] max-w-md bg-white rounded-2xl p-6"
        aria-describedby="training-goal-description"
      >
        <DialogTitle className="sr-only">
          {isEditing ? 'Edit Training Goal' : 'Set Training Goal'}
        </DialogTitle>
        <div id="training-goal-description" className="sr-only">
          {isEditing ? 'Edit your monthly training goal' : 'Set a new monthly training goal'}
        </div>
        <SossietyDialogHeader title={isEditing ? 'Edit Training Goal' : 'Set Training Goal'}>
          <SossietyDialogClose onClick={onClose} />
        </SossietyDialogHeader>

        {step === 1 ? (
          // Step 1: Goal Input
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-bold text-black mb-2">
                Monthly Goal
              </h2>
              <p className="text-gray-600 text-sm">
                How many trainings do you want to complete this month? Consistency is the key to progress!
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-black text-sm font-medium mb-2">
                  Monthly Training Goal
                </label>
                <Input
                  type="number"
                  value={monthlyGoal}
                  onChange={(e) => setMonthlyGoal(e.target.value)}
                  placeholder="Ex: 12, 16, 20..."
                  className="bg-gray-50 border-gray-300 text-black placeholder:text-gray-500"
                  min="1"
                />
                {error && (
                  <p className="text-red-500 text-sm mt-1">{error}</p>
                )}
              </div>

              <Button
                onClick={handleNext}
                disabled={!monthlyGoal || parseInt(monthlyGoal) < 1}
                className="w-full bg-[#D72E16] hover:bg-[#B91C1C] text-white rounded-full"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        ) : (
          // Step 2: Confirmation and Visualization
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-bold text-black mb-2">
                Goal Set!
              </h2>
              <p className="text-gray-600 text-sm">
                Great! To achieve your goal of {monthlyGoal} trainings, you'll need focus and consistency.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <h3 className="text-black font-medium mb-2">
                Your weekly goal will be:
              </h3>
              <div className="text-3xl font-bold text-[#D72E16] mb-2">
                {weeklyGoal}
              </div>
              <p className="text-gray-600 text-sm">
                Train {weeklyGoal} times per week to earn a new stripe on your goal belt!
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex-1 border-black text-black hover:bg-gray-50 rounded-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={isCreating || isUpdating}
                className="flex-1 bg-[#D72E16] hover:bg-[#B91C1C] text-white rounded-full"
              >
                {isCreating || isUpdating ? 'Saving...' : 'Confirm and Start'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
